import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useMyTeam } from "@coworker/app/src/core/hooks/useStoreTeams";
import {
  AssignToModal,
  OnConfirmClickResult,
} from "@coworker/apprestructured/src/task-management/components/AssignTo/AssignToModal";
import CloseSheet from "@coworker/apprestructured/src/task-management/components/CloseSheet/CloseSheet";
import { TaskListItem } from "@coworker/apprestructured/src/TasksOverview/hooks/useTasksOverview";
import { useTranslation } from "@coworker/locales";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { useEffect } from "react";
import { TEAM_DELETION_FLOW_OPERATIONS } from "../../hooks/TeamDeletionOperations";
import { OperationStepProps } from "../types";
import {
  DeleteTeamTasksPrompt,
  TeamTasksActionOption,
} from "./components/DeleteTeamTasksPrompt/DeleteTeamTasksPrompt";
import { useAssignToModal } from "./hooks/useAssignToModal";
import { useCloseSheetModal } from "./hooks/useCloseSheetModal";
import { useModal } from "./hooks/useModal";

type TasksStepProps = OperationStepProps & {
  visible: boolean;
  team: StoreTeam;
  tasks: TaskListItem[];
};

export const TasksStep = ({
  visible,
  team,
  tasks,
  goToNextStep,
  goToPreviousStep,
  teamDeletionFlowOperations,
}: TasksStepProps) => {
  const myTeam = useMyTeam();
  const { t } = useTranslation();
  const currentUserUid = useUID();

  const deleteTeamTasksModal = useModal(false);
  const closeSheetModal = useCloseSheetModal(tasks);
  const assignToModal = useAssignToModal(team.id);

  const closeAllModals = () => {
    deleteTeamTasksModal.close();
    closeSheetModal.close();
    assignToModal.close();
  };

  useEffect(() => {
    if (visible) {
      deleteTeamTasksModal.open();
    } else {
      closeAllModals();
    }
    // we only want to run this effect when the `visible` prop changes, so we disable the exhaustive-deps rule for this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleCloseSheetModalClose = () => {
    closeSheetModal.close();
    deleteTeamTasksModal.open();
  };

  const handleCloseTasks = () => {
    teamDeletionFlowOperations.register({
      type: TEAM_DELETION_FLOW_OPERATIONS.CLOSE_TASKS,
      payload: {
        tasks,
        closeReasonId: closeSheetModal.closeReasonId || "",
        description: closeSheetModal.description,
        currentUserUid,
        storeId: team.storeId,
      },
    });
    goToNextStep();
  };

  const handleAssignToTeam = (result: OnConfirmClickResult) => {
    teamDeletionFlowOperations.register({
      type: TEAM_DELETION_FLOW_OPERATIONS.REASSIGN_TASKS,
      payload: {
        tasks,
        teamId: result.teamId,
        userId: result.userId,
        currentUserUid,
      },
    });
    goToNextStep();
  };

  const handleCloseAssignToModal = () => {
    assignToModal.close();
    deleteTeamTasksModal.open();
  };

  const handleTasksDispositionSelection = (option: TeamTasksActionOption) => {
    deleteTeamTasksModal.close();
    if (option === TeamTasksActionOption.REASSIGN) {
      assignToModal.open();
    } else if (option === TeamTasksActionOption.CLOSE) {
      closeSheetModal.open();
    }
  };

  const handleCloseDeleteTeamTasksFlow = () => {
    closeAllModals();
    goToPreviousStep();
  };

  return (
    <>
      <DeleteTeamTasksPrompt
        isVisible={deleteTeamTasksModal.shown}
        onClose={handleCloseDeleteTeamTasksFlow}
        onConfirm={handleTasksDispositionSelection}
        taskCount={tasks.length}
      />

      <CloseSheet
        title={t("closeString")}
        subtitle={t("selectCloseReasonString")}
        buttonText={t("closeString")}
        options={closeSheetModal.closeReasonOptions}
        closeReasonId={closeSheetModal.closeReasonId}
        onCloseReasonIdChange={closeSheetModal.setCloseReasonId}
        description={closeSheetModal.description}
        onDescriptionChange={closeSheetModal.setDescription}
        visible={closeSheetModal.shown}
        onClose={handleCloseSheetModalClose}
        onCloseTask={handleCloseTasks}
      />

      <AssignToModal
        visible={assignToModal.shown}
        searchQuery={assignToModal.searchQuery}
        onSearchQueryChange={assignToModal.setSearchQuery}
        isTeamsLoading={assignToModal.isTeamsLoading}
        isUsersLoading={assignToModal.isActiveUsersLoading}
        teams={assignToModal.assignableTeams}
        users={assignToModal.activeUsers}
        myTeamId={myTeam?.id || ""}
        onConfirmClick={handleAssignToTeam}
        onClose={handleCloseAssignToModal}
      />
    </>
  );
};
